import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import CardImage from '../components/CardImage';
import { cardsApi } from '../api/cardsApi';
import { decksApi } from '../api/decksApi';
import { useAuth } from '../auth/AuthContext';
import { banStatus, BAN_BADGE } from '../banlist';
import { cardImageUrl } from '../config';
import type { Card, CardReference, SynergyEntry } from '../../../shared/types';

/** Displays a stat that may be "?" for cards with variable ATK/DEF. */
function statValue(value: number | null, raw: string | null | undefined): string {
  if (value != null) return String(value);
  if (raw === '?') return '?';
  return '—';
}

function ReferenceLinks({ title, refs }: { title: string; refs: CardReference[] }) {
  if (!refs || refs.length === 0) return null;
  return (
    <section>
      <h3>{title}</h3>
      <div className="ref-links">
        {refs.map(r => (
          <Link key={r.name} to={`/card/${encodeURIComponent(r.name)}`} className="badge ref-badge">
            {r.name}
          </Link>
        ))}
      </div>
    </section>
  );
}

/** A grid of synergy partners, each a mini card image + name + score. */
function SynergyList({
  title, entries, note, emptyText,
}: { title: string; entries: SynergyEntry[]; note?: string; emptyText?: string }) {
  if ((!entries || entries.length === 0) && !emptyText) return null;
  return (
    <section className="synergy">
      <h3>{title}</h3>
      {note && <p className="muted syn-note">{note}</p>}
      {entries && entries.length > 0 ? (
        <div className="syn-grid">
          {entries.map(e => (
            <Link
              key={e.name}
              to={`/card/${encodeURIComponent(e.name)}`}
              className="syn-card"
              title={`Appears together in ${e.coDecks} deck${e.coDecks === 1 ? '' : 's'}`}
            >
              <div className="syn-card-img">
                <CardImage src={cardImageUrl(e.image)} alt={e.name} />
                {banStatus(e.name) && (
                  <span
                    className={`ban-badge ${BAN_BADGE[banStatus(e.name)!].cls}`}
                    title={BAN_BADGE[banStatus(e.name)!].title}
                  >
                    {BAN_BADGE[banStatus(e.name)!].symbol}
                  </span>
                )}
                <span className="syn-card-score">{e.score}%</span>
              </div>
              <span className="syn-card-name">{e.name}</span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="muted">{emptyText}</p>
      )}
    </section>
  );
}

export default function CardDetailPage() {
  const { name = '' } = useParams<{ name: string }>();
  const { user, getToken, refresh } = useAuth();
  const navigate = useNavigate();
  const [card, setCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [printingsOpen, setPrintingsOpen] = useState(false);
  const [buildingDeck, setBuildingDeck] = useState(false);

  async function buildDeckWithCard() {
    if (!card) return;
    setBuildingDeck(true);
    // Skill cards seed the skill slot; everything else seeds the main deck.
    const isSkill = card.category === 'Skill';
    try {
      const deck = await decksApi.create(
        {
          title: `${card.name} deck`,
          privacy: 'private',
          skill: isSkill ? card.name : undefined,
          mainDeck: isSkill ? undefined : [{ name: card.name, count: 1 }],
          coverCard: card.name,
        },
        getToken, refresh,
      );
      navigate(`/deck/${deck.id}/${deck.slug}`);
    } catch {
      setBuildingDeck(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setCard(null);
    setSelectedIdx(0);
    setPrintingsOpen(false);
    cardsApi
      .get(name)
      .then(c => { if (!cancelled) setCard(c); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Card not found'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [name]);

  const printings = card?.printings ?? [];
  const selected = printings[selectedIdx];

  return (
    <div>
      <SiteHeader />
      <main className="page page--wide">
        <p style={{ marginBottom: 16 }}>
          <Link to="/">← Back to search</Link>
        </p>

        {loading && <p className="muted">Loading…</p>}
        {error && !loading && (
          <div>
            <h1>Card not found</h1>
            <p className="error">{error}</p>
          </div>
        )}

        {card && !loading && (
          <>
            <div className="detail-head">
              <div className="detail-img">
                {printingsOpen && printings.length > 0 ? (
                  <div className="printing-picker">
                    <div className="printing-picker-head">
                      <span>Select a printing</span>
                      <button type="button" className="link-btn" onClick={() => setPrintingsOpen(false)}>
                        ✕ Close
                      </button>
                    </div>
                    <div className="printing-picker-grid">
                      {printings.map((p, i) => (
                        <button
                          type="button"
                          key={`${p.setNumber ?? 'p'}-${i}`}
                          className={`printing-option${i === selectedIdx ? ' selected' : ''}`}
                          onClick={() => { setSelectedIdx(i); setPrintingsOpen(false); }}
                        >
                          <CardImage src={cardImageUrl(p.imageFile)} alt={p.setNumber ?? 'Printing'} />
                          <span className="printing-option-label">{p.setNumber ?? '—'}</span>
                          {p.rarity && <span className="muted printing-option-rarity">{p.rarity}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <CardImage
                    src={cardImageUrl(selected?.imageFile ?? card.primaryImage)}
                    alt={card.nameDisplay ?? card.name}
                  />
                )}

                {printings.length > 1 && !printingsOpen && (
                  <button type="button" className="printings-toggle" onClick={() => setPrintingsOpen(true)}>
                    View all {printings.length} printings ▾
                  </button>
                )}

                {selected && (
                  <div className="current-printing">
                    {selected.setNumber && <span className="badge">{selected.setNumber}</span>}
                    {selected.rarity && <span className="badge">{selected.rarity}</span>}
                    {selected.setName && <div className="current-printing-set">{selected.setName}</div>}
                    {selected.released && <div className="muted">Released {selected.released}</div>}
                    {selected.tcgplayerUrl && (
                      <a href={selected.tcgplayerUrl} target="_blank" rel="noreferrer">
                        View on TCGplayer ↗
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="detail-info">
                <h1>{card.nameDisplay ?? card.name}</h1>
                <div className="detail-badges">
                  <span className="badge">{card.category}</span>
                  {card.attribute && <span className="badge">{card.attribute}</span>}
                  {card.level != null && <span className="badge">Level {card.level}</span>}
                  {card.passcode && <span className="badge badge-muted">#{card.passcode}</span>}
                  {banStatus(card.name) && (
                    <span className={`badge banlist-badge ${BAN_BADGE[banStatus(card.name)!].cls}`}>
                      {BAN_BADGE[banStatus(card.name)!].title}
                    </span>
                  )}
                </div>
                {card.types && card.types.length > 0 && (
                  <p className="detail-typeline">[ {card.types.join(' / ')} ]</p>
                )}
                {card.category === 'Monster' && (
                  <p className="detail-stats">
                    <strong>ATK</strong> {statValue(card.atk, printings[0]?.atkRaw)}
                    {'   '}
                    <strong>DEF</strong> {statValue(card.def, printings[0]?.defRaw)}
                  </p>
                )}
                {card.effect && (
                  <div className="detail-effect" style={{ whiteSpace: 'pre-line' }}>
                    {card.effect}
                  </div>
                )}
                {user && (
                  <button
                    type="button"
                    className="create-deck-btn"
                    style={{ marginTop: 14 }}
                    onClick={buildDeckWithCard}
                    disabled={buildingDeck}
                  >
                    {buildingDeck ? 'Creating…' : '+ Build a deck with this card'}
                  </button>
                )}
              </div>
            </div>

            <ReferenceLinks title="Cards referenced in effect" refs={card.effectReferences} />
            <ReferenceLinks title="Referenced by other cards" refs={card.mentionsInOtherCards} />

            {card.category !== 'Skill' && (
              <SynergyList
                title="Top Skill Cards"
                entries={card.topSkills}
                note="Skills most often paired with this card across Speed Duel decks."
              />
            )}
            <SynergyList
              title="Top Synergy"
              entries={card.topSynergy}
              note={
                card.deckCount > 0
                  ? `Cards most often built alongside this one — seen in ${card.deckCount} deck${card.deckCount === 1 ? '' : 's'}. Score = % of decks running either card that run both.`
                  : undefined
              }
              emptyText="Not enough deck data yet — this card wasn't seen in the scraped Speed Duel decks."
            />
          </>
        )}
      </main>
    </div>
  );
}
